import { ScrollableX } from '@/components/shared/Scrollable/ScrollableX';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { useLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useLinkClick';
import Link, { type InternalLinkProps } from '@/storybook/components/Link/Link';
import { LoadingSpinnerFallback } from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import { AppRoutes } from '@/utils';
import { LinkClickProps } from '@hooks/AdobeAnalyticsHooks/types/AdobeAnalyticsTypes';
import cn from 'classnames';
import { ComponentType, ReactNode, Suspense, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Styles from './NavigationTabList.module.css';

export type NavigationTab = {
  'id': string;
  'label': string;
  'link'?: AppRoutes;
  'onTabClick'?: (tab?: NavigationTab) => void;
  'additionalContent'?: ReactNode;
  'component'?: ComponentType;
  'isLoading'?: boolean;
  'isDisabled'?: boolean;
  'data-testid'?: string;
  'data-label-testid'?: string;
  'linkProps'?: InternalLinkProps['linkProps'];
};

export type NavigationTabListProps = {
  'tabs': Array<NavigationTab>;
  'data-testid'?: string;
  'adobeTrackingAnalytics'?: {
    trackingPosition: LinkClickProps['position'];
    trackingPrefixName: LinkClickProps['name'];
  };
  'fullWidth'?: boolean;
  'disableUppercaseLabel'?: boolean;
} & (
  | { currentNavigationTab: NavigationTab; setCurrentNavigationTab: (navigationTab: NavigationTab) => void }
  | { currentNavigationTab?: undefined; setCurrentNavigationTab?: undefined }
);

export default function NavigationTabList({
  tabs,
  currentNavigationTab,
  setCurrentNavigationTab,
  adobeTrackingAnalytics,
  'data-testid': dataTestId = 'channel-tab-list',
  fullWidth = false,
  disableUppercaseLabel = false,
}: NavigationTabListProps) {
  const location = useLocation();
  const { trackLinkClick } = useLinkClick();

  const handleTabClick = useCallback(
    (channelTab: NavigationTab) => {
      if (adobeTrackingAnalytics) {
        trackLinkClick({
          innertext: channelTab.label,
          type: AdobeAnalyticsConst.LINK_TYPES.TABS,
          name: `${adobeTrackingAnalytics.trackingPrefixName} - ${channelTab.label}`,
          position: adobeTrackingAnalytics.trackingPosition,
        });
      }
      setCurrentNavigationTab?.(channelTab);
    },
    [adobeTrackingAnalytics, setCurrentNavigationTab, trackLinkClick],
  );

  const activeNavigationTab: NavigationTab | undefined = useMemo(() => {
    if (currentNavigationTab) return currentNavigationTab;
    return tabs.find(channelTab => channelTab.link === location.pathname);
  }, [tabs, currentNavigationTab, location.pathname]);

  if (!activeNavigationTab) return null;

  return (
    <>
      <div className={Styles.navigationTabsContainer} data-testid={dataTestId}>
        <ScrollableX containerClassName={Styles.navigationTabSwitcher} scrollDistance={800}>
          {tabs.map(navigationTab => (
            <div
              id={navigationTab.id}
              key={navigationTab.id}
              className={cn(Styles.navigationTab, { [Styles.fullWidth]: fullWidth })}
              onClick={() => !navigationTab.isDisabled && handleTabClick(navigationTab)}
              data-is-navigation-tab-active={navigationTab.id === activeNavigationTab.id}
              data-is-navigation-tab-disabled={navigationTab.isDisabled}
              data-testid={navigationTab['data-testid'] + '-tab'}
            >
              <Link
                to={navigationTab.link}
                className={Styles.navigationTabLink}
                disabled={navigationTab.isDisabled}
                hideTextDecoration
                onClick={() => !navigationTab.isDisabled && navigationTab.onTabClick?.(navigationTab)}
                data-testid={navigationTab['data-testid']}
                linkProps={navigationTab.linkProps}
              >
                <section className={Styles.navigationTabContent}>
                  <div
                    className={cn(
                      Styles.navigationTabLabel,
                      { [Styles.uppercaseLabel]: !disableUppercaseLabel },
                      { [Styles.disableUppercaseLabel]: disableUppercaseLabel },
                    )}
                    data-testid={navigationTab['data-label-testid'] ?? navigationTab['data-testid'] + '-tab-label'}
                  >
                    {navigationTab.label}
                  </div>
                  {navigationTab.additionalContent && (
                    <div
                      className={Styles.navigationTabAdditionalContent}
                      data-testid={navigationTab['data-testid'] + '-additionalContent'}
                    >
                      {navigationTab.additionalContent && navigationTab.isLoading && (
                        <div
                          className={Styles.navigationTabLoadingOverlay}
                          data-testid="navigation-tab-loading-overlay"
                        >
                          <LoadingSpinnerFallback size={24} />
                        </div>
                      )}
                      {navigationTab.additionalContent}
                    </div>
                  )}
                </section>
              </Link>
            </div>
          ))}
        </ScrollableX>
      </div>
      {activeNavigationTab.component && (
        <Suspense fallback={<LoadingSpinnerFallback />}>
          <activeNavigationTab.component />
        </Suspense>
      )}
    </>
  );
}
